import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import useStore from '../store/useStore';
import { useFormData, useApiCall } from '../hooks';
import TwoFactor from './TwoFactor';

const PersonIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const setAuth = useStore((state) => state.setAuth);

  const { values, handleChange } = useFormData({
    username: '',
    password: '',
  });
  const { isLoading, error, execute, clearError } = useApiCall();

  const [focusedField, setFocusedField] = useState(null);
  // After the password: the second step, to set up an authenticator or enter a code.
  const [second, setSecond] = useState(null);

  const signedIn = (data) => {
    setAuth(data.user, data.token, data.companies || []);
    navigate('/');
  };

  const handleLogin = async () => {
    const result = await execute(authAPI.login, values.username, values.password);

    if (result.success) {
      if (result.data.twoFactor) setSecond({ step: result.data.twoFactor, challenge: result.data.challenge });
      else signedIn(result.data);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin();
    }
  };

  const isFieldActive = (field) => focusedField === field || values[field];

  return (
    <div className="login-container">
      <div className="login-box">
        <img src="/kinspeed-logo.png" alt="Kinspeed" className="login-logo" />

        {second ? (
          <TwoFactor step={second.step} challenge={second.challenge} onSignedIn={signedIn} onCancel={() => setSecond(null)} />
        ) : (
        <>
        <h4 className="login-greeting">
          <span className="login-hello">Hello,</span>
          <br />
          Welcome back
        </h4>

        {error && (
          <p className="login-error">{error}</p>
        )}

        <div className={`login-field ${isFieldActive('username') ? 'active' : ''} ${focusedField === 'username' ? 'focused' : ''}`}>
          <input
            type="text"
            id="username"
            name="username"
            value={values.username}
            onChange={(e) => {
              handleChange(e);
              clearError();
            }}
            onFocus={() => setFocusedField('username')}
            onBlur={() => setFocusedField(null)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            autoFocus
          />
          <label htmlFor="username">Username</label>
          <div className="login-field-icon">
            <PersonIcon />
          </div>
        </div>

        <div className={`login-field ${isFieldActive('password') ? 'active' : ''} ${focusedField === 'password' ? 'focused' : ''}`}>
          <input
            type="password"
            id="password"
            name="password"
            value={values.password}
            onChange={(e) => {
              handleChange(e);
              clearError();
            }}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <label htmlFor="password">Password</label>
          <div className="login-field-icon">
            <LockIcon />
          </div>
        </div>

        <label className="login-remember">
          <input type="checkbox" />
          <span>Remember me</span>
        </label>

        <div className="login-actions">
          <div className={`login-spinner-container ${isLoading ? 'visible' : ''}`}>
            <div className="login-spinner"></div>
          </div>
          <button
            type="button"
            className="login-btn"
            onClick={handleLogin}
            disabled={isLoading || !values.username || !values.password}
          >
            Login
          </button>
        </div>
        </>
        )}
        </div>
    </div>
  );
};

export default Login;
